<template>
  <div class="product__greeting-card" v-if="productGrettingCardVariants.length">
    <div class="greeting-card__title">
      <template v-if="title">{{ title }}</template>
      <template v-else>
        Choose your greeting card (+ {{ formatPrice(firstVariant.price) }} USD)
        <span
          class="greeting-card__tag-sales"
          v-if="firstVariant.compareAtPrice && percentDiscount"
          >Sale {{ percentDiscount }}%
        </span>
        <del
          class="greeting-card__compare-price"
          v-if="firstVariant.compareAtPrice"
          >{{ formatPrice(firstVariant.compareAtPrice) }} USD</del
        >
      </template>
    </div>
    <div class="greeting-card__items">
      <div
        class="greeting-card__item"
        :class="{
          'greeting-card__item--active': indexChoosed == index,
        }"
        v-for="(item, index) of productGrettingCardVariants"
        :key="index"
        @click="selecteProductVariant(index)"
      >
        <sib-image v-bind="getImageProductVariant(item)"></sib-image>
      </div>
      <div
        class="greeting-card__item greeting-card__item--no-thanks"
        :class="{
          'greeting-card__item--active-no-thanks':
            indexChoosed == productGrettingCardVariants.length,
        }"
        @click="selecteProductVariant(productGrettingCardVariants.length)"
      >
        No, Thanks
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import { NullOrType, Product } from '@/types'
import { formatPrice } from '@/utils/product'
import { computed, onMounted, ref, watch } from 'vue'
import { QUERY_STRING_NO_CACHE } from '@/utils'
import { useRoute } from 'vue-router'

interface ProductGreetingCardExtraProps {
  handle: string
  title: string
}

const props = withDefaults(defineProps<ProductGreetingCardExtraProps>(), {
  title: '',
  handle: '',
})

const emit = defineEmits<{
  (e: 'greeting-card-selected', value: any): void
}>()

const route = useRoute()
const productStore = useProductStore()

const product = ref<NullOrType<Product>>(null)
const indexChoosed = ref(-1)
const variantSelected = ref(null)
const firstVariant = ref(null)

const productGrettingCardVariants = computed(
  () => product.value?.variants || []
)

const percentDiscount = computed(() => {
  if (!firstVariant.value || !firstVariant.value.compareAtPrice) return 0
  return Math.round(
    ((firstVariant.value.compareAtPrice - firstVariant.value.price) /
      firstVariant.value.compareAtPrice) *
      100
  )
})

onMounted(() => {
  getProductByHandle()

  watch(
    () => variantSelected.value,
    () => {
      emit('greeting-card-selected', {
        greetingCardVariantSelected: variantSelected.value,
        quantity: 1,
        productHandle: props.handle,
      })
    }
  )
})

async function getProductByHandle() {
  if (!props.handle) return
  const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
  try {
    product.value = await productStore.getProductByHandle({
      handle: props.handle,
      isForceLoadNewData,
    })
    if (product.value?.variants[0]) {
      firstVariant.value = product.value?.variants[0]
    }
  } catch (error) {}
}

function selecteProductVariant(index: number) {
  indexChoosed.value = index
  if (!productGrettingCardVariants.value?.length) return
  if (
    indexChoosed.value == productGrettingCardVariants.value?.length ||
    indexChoosed.value == -1
  ) {
    variantSelected.value = null
  } else {
    variantSelected.value = productGrettingCardVariants.value[index]
  }
}

function getImageProductVariant(productVariant: any) {
  return product.value?.images?.find(
    (item) => item.id == productVariant?.image?.id
  )
}
</script>

<style lang="scss">
.product__greeting-card {
  background: var(--background-color);
  padding: 0 1rem 2rem 1rem;
  .greeting-card {
    &__items {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(62px, 1fr));
      gap: 9px;
      margin: 5px;
    }
    &__item {
      background-color: transparent;
      box-sizing: border-box;
      box-shadow: 0 0 2px 1px #0003;
      border: 3px solid #fff;
      width: 62px;
      height: 62px;
      transition: all 0.3s ease;
      cursor: pointer;
      &--active {
        border-color: #2f80ed;
        width: 204px;
        height: 204px;
        grid-column: span 3;
        grid-row: span 3;
      }
      &--active-no-thanks {
        border-color: #2f80ed;
      }
      &--no-thanks {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-weight: 600;
      }
      &--active,
      &--active-no-thanks {
        position: relative;
        &::before {
          content: '';
          position: absolute;
          width: 12px;
          height: 12px;
          right: 2px;
          bottom: 0;
          background: url('@/assets/images/icons/check-mark.svg');
          background-repeat: no-repeat;
          background-size: cover;
          z-index: var(--base-z-index);
        }
      }
    }
    &__compare-price {
      font-size: 12px;
      color: var(--product-compare-at-price-color);
      margin-left: 5px;
    }
    &__title {
      font-size: 1em;
      font-weight: 600;
      margin-bottom: 1rem;
      margin-left: 4px;
    }
    &__tag-sales {
      color: #ffffff;
      font-size: 10px;
      vertical-align: middle;
      font-weight: 600;
      background: var(--color-secondary);
      border-radius: 4px;
      white-space: nowrap;
      padding: 3px 8px 4px 8px;
    }
  }
}
</style>
